import { Button, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DashboardModal } from '@/pages/CustomerDashboard/DashboardModal';
import { formatHurdleText } from '@/pages/FarmerList/helper';
import { Color } from '@/themes/MUITheme/palette';
import { OrderTransactionsApi } from '@/utilities/api/OrderTransactionsApi';
import { getPointsEntryLabel } from '@/utilities/OrderLabels';
import { ApiSubwallet } from '@api/interfaces';
import { ApiOrderTransaction } from '@api/interfaces/ApiOrderTransaction';
import { ApiPointsHistory } from '@api/interfaces/ApiPointsHistory';
import { Box, Divider, Stack, useTheme } from '@mui/material';
import { OrderTransactionSubtype } from '@shared/enums';
import { RewardsUom } from '@shared/enums/RewardsUom';
import { formatCurrency, formatNumber, formatMediumDate, getAccountName } from '@shared/utilities';
import {
  getMostRecentDescription,
  getPointsTotalsByType,
  PointsTotalsByType,
} from '@shared/utilities/PointsUtils';
import { isNil, round } from 'lodash';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

type PointsPerSubwallet = Map<string, PointsTotalsByType>;

interface PointsEntry {
  amount: number,
  id: string,
  label: string,
  /** Only a manual grant carries one; it stays its own row so it keeps its own edit control. */
  manualPointsHistory?: ApiPointsHistory,
  subwalletName?: string,
}

function getSubwalletName (
  history: ApiPointsHistory,
  subwallets: ApiSubwallet[],
): string | undefined {
  return history.subwallet?.name
    ?? subwallets.find(({ id }) => id === history.subwalletId)?.name;
}

function accumulatePointsPerSubwallet (
  pointHistories: ApiPointsHistory[],
  subwallets: ApiSubwallet[],
): PointsPerSubwallet {
  const result: PointsPerSubwallet = new Map();
  pointHistories.forEach((history) => {
    const subwalletName = getSubwalletName(history, subwallets);
    if (!subwalletName) {
      return;
    }
    const subwalletPoints = result.get(subwalletName)
      ?? { earned: 0, redeemed: 0, refunded: 0, returned: 0 };
    const totals = getPointsTotalsByType([history]);
    subwalletPoints.earned += totals.earned;
    subwalletPoints.redeemed += totals.redeemed;
    subwalletPoints.refunded += totals.refunded;
    subwalletPoints.returned += totals.returned;
    result.set(subwalletName, subwalletPoints);
  });
  return result;
}

interface OrderDetailsModalProps {
  accessToken?: string,
  brandColor?: string,
  forceMobileView?: boolean,
  onClose: () => void,
  /** Supplied only by internal views; farmers never see points entries or edit controls. */
  onEditManualPoints?: (pointsHistory: ApiPointsHistory) => void,
  orderId: string,
  subwallets: ApiSubwallet[],
}

export const OrderDetailsModal = ({
  accessToken,
  brandColor,
  forceMobileView = false,
  onClose,
  onEditManualPoints,
  orderId,
  subwallets,
}: OrderDetailsModalProps) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { isMobile } = useMediaQuery();
  const displayMobileView = forceMobileView || isMobile;
  const brandColorBg = brandColor ?? theme.palette.primary.main;

  const { data: order } = useQuery(
    [QueryKeys.GET_ORDER, orderId],
    async () => OrderTransactionsApi.getOrder(orderId, accessToken),
  );

  const customer = order?.farmer;

  const formattedDate = order && formatMediumDate(order.orderDate, i18n);
  const formattedTotal = order && formatCurrency(order.totalCost, i18n);

  const formattedQuantity = useMemo(() => {
    if (isNil(order?.orderSubtype)) {
      return null;
    }
    let result = 0;
    order?.products.forEach(({ quantity }) => {
      if (quantity) {
        result += quantity;
      }
    });
    const uom = order?.products?.[0]?.uom ?? '';
    return formatHurdleText(result, uom as RewardsUom, i18n);
  }, [order]);

  const pointsPerSubwallet: PointsPerSubwallet = useMemo(() => (
    accumulatePointsPerSubwallet(order?.pointHistories ?? [], subwallets)
  ), [order?.pointHistories, subwallets]);

  let orderTypeText: string;
  if (order?.orderSubtype === OrderTransactionSubtype.SCALE_TICKET) {
    orderTypeText = t($ => $.common.order.scaleTicket);
  } else if (order?.orderSubtype === OrderTransactionSubtype.SETTLEMENT) {
    orderTypeText = t($ => $.common.order.settlement);
  } else {
    orderTypeText = t($ => $.common.order.order);
  }

  const bookingPointHistoriesMap = useMemo(() => {
    const map = new Map<ApiOrderTransaction, PointsPerSubwallet>();
    const pointHistoriesByProductId = new Map<string, ApiPointsHistory[]>();
    (order?.pointHistories ?? []).forEach((history) => {
      const productId = history.orderTransactionProductId;
      if (!productId) {
        return;
      }
      if (!pointHistoriesByProductId.has(productId)) {
        pointHistoriesByProductId.set(productId, []);
      }
      pointHistoriesByProductId.get(productId)!.push(history);
    });

    const bookingToProductsMap = new Map<ApiOrderTransaction, ApiOrderTransaction['products']>();
    order?.products?.forEach((product) => {
      if (product.linkedBooking) {
        const booking = product.linkedBooking as ApiOrderTransaction;
        if (!bookingToProductsMap.has(booking)) {
          bookingToProductsMap.set(booking, []);
        }
        bookingToProductsMap.get(booking)!.push(product);
      }
    });

    bookingToProductsMap.forEach((products, booking) => {
      const histories: ApiPointsHistory[] = [];
      products?.forEach((product) => {
        const productHistories = pointHistoriesByProductId.get(product.id) ?? [];
        histories.push(...productHistories);
      });
      map.set(booking, accumulatePointsPerSubwallet(histories, subwallets));
    });
    return map;
  }, [order?.pointHistories, order?.products, subwallets]);

  const mostRecentDescription = useMemo(
    () => getMostRecentDescription(order?.pointHistories ?? []),
    [order?.pointHistories],
  );

  const pointsEntries: PointsEntry[] = useMemo(() => {
    const histories = [...(order?.pointHistories ?? [])].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );
    const entriesByKey = new Map<string, PointsEntry>();
    histories.forEach((history) => {
      const label = (history.isManuallyIssued && history.description)
        ? history.description
        : getPointsEntryLabel(history.type, t);
      const subwalletName = getSubwalletName(history, subwallets);
      const key = history.isManuallyIssued
        ? history.id
        : `${label}|${history.subwalletId}`;
      const entry = entriesByKey.get(key);
      if (entry) {
        entry.amount = round(entry.amount + history.amount, 2);
        return;
      }
      entriesByKey.set(key, {
        amount: history.amount,
        id: history.id,
        label,
        manualPointsHistory: history.isManuallyIssued ? history : undefined,
        subwalletName,
      });
    });
    return [...entriesByKey.values()];
  }, [order?.pointHistories, subwallets, t]);

  const canEditManualPoints = !!onEditManualPoints
    && pointsEntries.some(({ manualPointsHistory }) => !!manualPointsHistory);

  const pointTypes = ['earned', 'redeemed', 'returned', 'refunded'] as (keyof PointsTotalsByType)[];
  const nonZeroPointTypes = pointTypes.filter((pointType) => (
    [...pointsPerSubwallet.values()].some((totals) => totals[pointType] !== 0)
  ));

  const BookingRedemptionSection = (
    <Stack alignSelf="flex-start" width="100%">
      {[...bookingPointHistoriesMap.entries()]
        .filter(([, bookingPointsPerSubwallet]) => (
          [...bookingPointsPerSubwallet.values()].some((totals) => totals.redeemed !== 0)
        ))
        .map(([booking, bookingPointsPerSubwallet]) => (
          <Box key={booking.groupingId}>
            <VSpacer size="1"/>
            <Text category="title-small">
              {t($ => $.orders.details.booking, { bookingId: booking.groupingId })}
            </Text>
            <Stack ml="8px" py="6px">
              {[...bookingPointsPerSubwallet.entries()].map(([name, totals]) => {
                if (totals.redeemed === 0) {
                  return null;
                }
                return (
                  <Stack direction="row" justifyContent="space-between" key={name}>
                    <Text category="body-medium">{name}</Text>
                    <Text category="title-small" color="error">
                      {formatNumber(totals.redeemed, i18n)}
                    </Text>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        ))
      }
    </Stack>
  );

  const PointsEntriesSection = (
    <Stack alignSelf="flex-start" width="100%">
      <VSpacer size="5"/>
      <Divider sx={{ width: '100%' }}/>
      <VSpacer size="5"/>
      <Text category="title-medium">
        {t($ => $.orders.details.pointsEntries)}
      </Text>
      {pointsEntries.map((entry) => {
        const isPositive = entry.amount >= 0;
        const pointsTextColor: Color = isPositive ? 'success' : 'error';
        const { manualPointsHistory } = entry;
        return (
          <Stack key={entry.id} py="6px">
            <Stack alignItems="center" direction="row" gap="16px" justifyContent="space-between">
              <Text category="body-medium">
                {entry.label}
              </Text>
              <Stack alignItems="center" direction="row" gap="12px">
                <Text category="title-small" color={pointsTextColor} whiteSpace="nowrap">
                  {isPositive ? '+' : '–'}{formatNumber(Math.abs(entry.amount), i18n)}
                </Text>
                {!!manualPointsHistory && (
                  <Button
                    onClick={() => onEditManualPoints?.(manualPointsHistory)}
                    square
                    testID={`order-details-edit-manual-points-${entry.id}`}
                    variant="outlined"
                  >
                    {t($ => $.common.actions.edit)}
                  </Button>
                )}
              </Stack>
            </Stack>
            {!!entry.subwalletName && (
              <Text category="body-small" sx={{ color: theme.palette.text.secondary }}>
                {entry.subwalletName}
              </Text>
            )}
          </Stack>
        );
      })}
    </Stack>
  );

  const PointsBreakdownSection = (
    <>
      {nonZeroPointTypes.map((pointType, index) => {
        let pointsTextColor: Color | undefined = undefined;
        if (pointType === 'earned') {
          pointsTextColor = 'success';
        } else if (pointType === 'redeemed') {
          pointsTextColor = 'error';
        }
        const isPositive = ['earned', 'refunded'].includes(pointType);
        let totalPoints = 0;
        pointsPerSubwallet.forEach((totals) => {
          totalPoints += totals[pointType];
        });

        return (
          <Fragment key={pointType}>
            <VSpacer size="5"/>
            <Stack width="100%">
              <Stack direction="row" justifyContent="space-between">
                <Text category="title-medium">
                  {t($ => $.common.order.totalPoints[pointType])}
                </Text>
                <Stack direction="row" gap="2px">
                  <Text category="title-medium" color={pointsTextColor}>
                    {isPositive ? '+' : '–'}
                  </Text>
                  <Text category="title-medium" color={pointsTextColor}>
                    {formatNumber(Math.abs(totalPoints), i18n)}
                  </Text>
                </Stack>
              </Stack>
              <VSpacer size="1"/>
              {[...pointsPerSubwallet].map(([name, totals]) => (
                totals[pointType] !== 0 && (
                  <Stack direction="row" justifyContent="space-between" key={name} py="6px">
                    <Text category="body-medium">
                      {name}
                    </Text>
                    <Text category="title-small" color={pointsTextColor}>
                      {formatNumber(Math.abs(totals[pointType]), i18n)}
                    </Text>
                  </Stack>
                )
              ))}
            </Stack>
            {pointType === 'redeemed' && BookingRedemptionSection}
            {index < nonZeroPointTypes.length - 1 && (
              <>
                <VSpacer size="5"/>
                <Divider sx={{ width: '100%' }}/>
              </>
            )}
          </Fragment>
        );
      })}
    </>
  );

  return (
    <DashboardModal
      closeButtonStyle={{
        bgcolor: brandColorBg,
        color: theme.palette.getContrastText(brandColorBg),
        '&:hover': { bgcolor: brandColorBg, opacity: 0.9, borderColor: 'inherit' },
        ...(displayMobileView ? {} : { alignSelf: 'self-end', width: 'fit-content' }),
      }}
      hideCloseButtonIcon
      onClose={onClose}
      open
      testID="order-details-modal"
      title={(
        <Text
          category="title-large"
          style={{
            lineBreak: 'anywhere',
            maxWidth: '80%',
            minWidth: 0,
            overflowWrap: 'anywhere',
            whiteSpace: 'normal',
          }}
        >
          {orderTypeText} {order?.groupingId}
        </Text>
      )}
      titleSx={displayMobileView ? undefined : { alignItems: 'flex-start', px: '20px' }}
    >
      <Stack
        alignItems={displayMobileView ? 'center' : 'flex-start'}
        px={displayMobileView ? '16px' : '20px'}
        width="100%"
      >
        <VSpacer size="3"/>
        <Stack alignItems="center" direction="row" flexWrap="wrap">
          <Text category="body-medium">
            {t($ => $.common.customerName, { name: customer && getAccountName(customer) })}
          </Text>
          <Box
            borderRadius="50%"
            height="4px"
            mx="8px"
            sx={{ bgcolor: theme.palette.text.primary }}
            width="4px"
          />
          <Text category="body-medium">
            {t($ => $.common.labels.erpId, { id: customer?.externalId })}
          </Text>
        </Stack>
        <VSpacer size="4"/>
        <Stack direction="row" gap="16px" width="100%">
          <Stack width="100%">
            <Text
              category="body-small"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t($ => $.common.table.date)}
            </Text>
            <Text>
              {formattedDate}
            </Text>
          </Stack>
          <Stack width="100%">
            <Text
              category="body-small"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t($ => $.common.order.total)}
            </Text>
            <Text>
              {formattedTotal}
            </Text>
          </Stack>
          {!!formattedQuantity && (
            <Stack width="100%">
              <Text
                category="body-small"
                sx={{ color: theme.palette.text.secondary }}
              >
                {t($ => $.common.order.quantity)}
              </Text>
              <Text>
                {formattedQuantity}
              </Text>
            </Stack>
          )}
        </Stack>
        <VSpacer size="5"/>
        <Divider sx={{ width: '100%' }}/>
        {!!mostRecentDescription && (
          <Stack alignSelf="flex-start" width="100%">
            <VSpacer size="5"/>
            <Text
              category="body-small"
              sx={{ color: theme.palette.text.secondary }}
            >
              {t($ => $.customers.transactions.description)}
            </Text>
            <Text category="body-medium">
              {mostRecentDescription}
            </Text>
          </Stack>
        )}
        {PointsBreakdownSection}
        {canEditManualPoints && PointsEntriesSection}
      </Stack>
    </DashboardModal>
  );
};
